/**
 * Mobile App Context — provides mobile-specific state and helpers
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { getPlatform, isNativePlatform } from '@/lib/native-features';

interface MobileContextType {
  isNative: boolean;
  platform: 'ios' | 'android' | 'web';
  isOnline: boolean;
  hasPendingSync: boolean;
  pendingSyncCount: number;
}

const MobileContext = createContext<MobileContextType>({
  isNative: false,
  platform: 'web',
  isOnline: true,
  hasPendingSync: false,
  pendingSyncCount: 0,
});

export function useMobileContext() {
  return useContext(MobileContext);
}

export function MobileProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const isNative = isNativePlatform();
  const platform = getPlatform();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Configure status bar and set CSS variable for its height
  useEffect(() => {
    if (!isNative) return;
    import('@capacitor/status-bar')
      .then(async ({ StatusBar, Style }) => {
        // Dark status bar (light text) since our header is dark
        await StatusBar.setStyle({ style: Style.Dark });

        // Get the actual status bar height and set it as a CSS variable
        const info = (await StatusBar.getInfo()) as any;
        if (info.height) {
          document.documentElement.style.setProperty('--status-bar-height', `${info.height}px`);
        }
      })
      .catch(() => {});
  }, [isNative]);

  // Handle Android hardware back button
  const router = useRouter();
  useEffect(() => {
    if (!isNative || platform !== 'android') return;

    let cleanup: (() => void) | undefined;
    import('@capacitor/app')
      .then(({ App }) => {
        const handler = App.addListener('backButton', () => {
          const { history } = router;
          if (history.length > 1) {
            history.back();
          } else {
            App.exitApp();
          }
        });
        cleanup = () => handler.then((h) => h.remove());
      })
      .catch(() => {});

    return () => cleanup?.();
  }, [isNative, platform, router]);

  // Check offline queue count periodically
  useEffect(() => {
    const checkQueue = async () => {
      const { getOfflineQueue } = await import('@/lib/native-features');
      const queue = await getOfflineQueue();
      setPendingSyncCount(queue.length);
    };
    checkQueue();
    const interval = setInterval(checkQueue, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <MobileContext.Provider
      value={{
        isNative,
        platform,
        isOnline,
        hasPendingSync: pendingSyncCount > 0,
        pendingSyncCount,
      }}
    >
      {children}
    </MobileContext.Provider>
  );
}
